/**
 * `useAssignAssetApprovalChain` mutation hook.
 *
 * Wraps the raw `assignAssetApprovalChain` service fn (per AD-23). On success it
 * invalidates:
 *   - the asset's own approval chain query (`ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail`) so the
 *     header control + details popover reflect the new assignment,
 *   - the asset's file detail, whose `status` the server reopens when an approved asset's
 *     chain is swapped for another,
 *   - every approval chain query under the project (`APPROVAL_CHAIN_QUERY_KEYS.all`) because
 *     a definition's `assetUsageCount` changes when it gains an asset,
 *   - the folder's files query so any folder-level aggregate refreshes, and
 *   - the folder list/detail aggregates (`invalidateFolderAggregates`)
 *     so the segmented progress bar reflects the shifted step breakdown.
 *
 * Side effects: NONE beyond invalidation + analytics. Toast/confirm UX belong to
 * the caller (the control owns error UX via SUPPRESS_ERROR_NOTIFICATION_HEADER).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/approval-chain';
import { track } from '@/shared/analytics';

import { assignAssetApprovalChain } from './asset-approval-chain.mutations.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { assignAssetApprovalChain };

interface UseAssignAssetApprovalChainOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after assigning. */
  folderId?: string;
}

interface AssignAssetApprovalChainVars {
  assetId: string;
  workflowDefinitionId: string;
}

export function useAssignAssetApprovalChain(options: UseAssignAssetApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ assetId, workflowDefinitionId }: AssignAssetApprovalChainVars) =>
      assignAssetApprovalChain(projectId, assetId, workflowDefinitionId),
    onSuccess: (_data, { assetId }) => {
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_workflow_assigned', { project_id: projectId });
    },
  });
}
